import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Link2, RefreshCw } from 'lucide-react';
import { useActiveEvent } from '../contexts/EventContext';
import { useAppSelector } from '../store';
import { API_BASE_URL, cn } from '../lib/utils';
import { hasPermission } from '../config/permissions';
import EventDisclosureReportView, {
  downloadDisclosureJson,
  printDisclosureReport,
} from '../components/command/EventDisclosureReportView';
import EventDisclosureShareModal from '../components/command/EventDisclosureShareModal';
import DisclosureFullReportModal from '../components/command/DisclosureFullReportModal';
import type {
  DisclosureDetailLevel,
  DisclosurePermissions,
  EventDisclosureReport,
  FullDisclosureRequest,
} from '../types/disclosure';

function disclosureFetchError(status: number, bodyError?: string): string {
  if (status === 404) {
    return 'Disclosure API not found — the server backend needs updating (deploy index.js and disclosure.js, then restart pm2).';
  }
  if (status === 403) return bodyError || 'Command access required to view disclosure reports.';
  if (status === 401) return 'Session expired — please log in again.';
  return bodyError || `Failed to load disclosure report (HTTP ${status})`;
}

export default function EventDisclosurePage() {
  const { activeEvent, activeEventId } = useActiveEvent();
  const { token, user } = useAppSelector((s) => s.auth);
  const [report, setReport] = useState<EventDisclosureReport | null>(null);
  const [shortReport, setShortReport] = useState<EventDisclosureReport | null>(null);
  const [permissions, setPermissions] = useState<DisclosurePermissions | null>(null);
  const [detailLevel, setDetailLevel] = useState<DisclosureDetailLevel>('short');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFullModal, setShowFullModal] = useState(false);
  const [fullBusy, setFullBusy] = useState(false);
  const [fullError, setFullError] = useState<string | null>(null);
  const [lastFullRequest, setLastFullRequest] = useState<FullDisclosureRequest | null>(null);

  const eventId = activeEvent?.id || activeEventId;
  const canViewFull = user
    ? hasPermission(user.role, user.customPermissions, 'disclosure:view_full')
    : false;

  const loadShortReport = async () => {
    if (!token || !eventId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/events/${eventId}/disclosure`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(disclosureFetchError(res.status, data.error));
      setShortReport(data.disclosure);
      setPermissions(data.permissions || null);
      setReport(data.disclosure);
      setDetailLevel('short');
      setLastFullRequest(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load disclosure report';
      setError(
        msg.includes('fetch') || msg === 'Failed to fetch'
          ? 'Could not reach the API — check your connection or backend server.'
          : msg,
      );
      setReport(null);
      setShortReport(null);
    } finally {
      setLoading(false);
    }
  };

  const loadFullReport = async (options: FullDisclosureRequest): Promise<boolean> => {
    if (!token || !eventId) return false;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/events/${eventId}/disclosure/full`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error || disclosureFetchError(res.status, data.error);
        setFullError(msg);
        return false;
      }
      setReport(data.disclosure);
      setPermissions(data.permissions || null);
      setDetailLevel('full');
      setLastFullRequest(options);
      setError(null);
      return true;
    } catch {
      setFullError('Could not reach the API — check your connection.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadShortReport();
  }, [token, eventId]);

  const handleSelectDetail = (level: DisclosureDetailLevel) => {
    if (level === 'short') {
      void loadShortReport();
      return;
    }
    if (!canViewFull) {
      setError('Full reports require the disclosure:view_full permission (assign in System Admin).');
      return;
    }
    setFullError(null);
    setShowFullModal(true);
  };

  const confirmFullReport = async (options: FullDisclosureRequest) => {
    setFullBusy(true);
    setFullError(null);
    const ok = await loadFullReport(options);
    if (ok) setShowFullModal(false);
    setFullBusy(false);
  };

  const handleRefresh = () => {
    if (detailLevel === 'full' && lastFullRequest) {
      void loadFullReport(lastFullRequest);
    } else {
      void loadShortReport();
    }
  };

  if (!eventId) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        No event selected. Open an event from Command Home first.
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-4 py-3 flex flex-wrap items-center gap-3 no-print">
        <Link
          to={`/workspace/incidents?eventId=${encodeURIComponent(eventId)}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to workspace
        </Link>
        <span className="text-xs font-mono text-muted-foreground">{eventId}</span>

        <div className="flex items-center rounded-lg border border-border overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => handleSelectDetail('short')}
            className={cn(
              'px-3 py-1.5 font-medium transition-colors',
              detailLevel === 'short' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
            )}
          >
            Short report
          </button>
          <button
            type="button"
            onClick={() => handleSelectDetail('full')}
            className={cn(
              'px-3 py-1.5 font-medium transition-colors border-l border-border',
              detailLevel === 'full' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
              !canViewFull && 'opacity-50',
            )}
            title={canViewFull ? 'Configure and generate full report sections' : 'Requires disclosure:view_full permission'}
          >
            Full report
          </button>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-border hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowShareModal(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Link2 className="w-3 h-3" />
            Share link
          </button>
        </div>
      </div>

      <div className="p-6">
        {!permissions?.includeClinical && report && (
          <p className="no-print mb-4 text-xs text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            Patient records show reference numbers only — you do not have <strong>prfs:view</strong> permission
            for clinical data. Assign this in System Admin if required.
          </p>
        )}
        {loading && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Building disclosure report…
          </p>
        )}
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3">
            {error}
          </div>
        )}
        {report && !loading && (
          <EventDisclosureReportView
            report={report}
            onPrint={printDisclosureReport}
            onDownload={() => downloadDisclosureJson(report)}
          />
        )}
      </div>

      {eventId && (
        <EventDisclosureShareModal
          open={showShareModal}
          eventId={eventId}
          eventName={activeEvent?.name || eventId}
          onClose={() => setShowShareModal(false)}
        />
      )}

      <DisclosureFullReportModal
        open={showFullModal}
        eventName={activeEvent?.name || eventId}
        shortReport={shortReport}
        includeClinical={!!permissions?.includeClinical}
        busy={fullBusy}
        error={fullError}
        onCancel={() => {
          if (fullBusy) return;
          setShowFullModal(false);
          setFullError(null);
        }}
        onConfirm={confirmFullReport}
      />
    </div>
  );
}
